import { Box, Typography, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { JSONContent } from '@tiptap/react';
import { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { InputSelectDialog } from 'components/FormControls/InputSelectDialog/InputSelectDialog';
import {
  ListSearchSelect,
  GenericItemLSS,
} from 'components/FormControls/ListSearchSelect/ListSearchSelect';
import RichTextInput, {
  richTextLabelTypographySx,
} from 'components/RichTextInput/RichTextInput';
import { useAssetEditorsContext } from '../../context/AssetEditorsContextProvider';
import { nonArticleRichTextInputSx } from '../../forms/AssetFormSx';
import { useAssetConfigContext } from '../../context/AssetConfigContextProvider';
import { UserAccountSummary } from 'utility/types/dataProvider';
import { isDeepEqual } from 'remeda';
import { CustomEditorOnChangeFn } from 'components/RichTextInput/utility/useDebouncedOnChange';

export type PayloadAuthor = { uuid?: string };

type AuthorsProps = {
  wrapperSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  showLabel?: boolean;
  label?: string;
};

// Non article styling for rich text input author chips
const getBaseRichTextInputSx = (theme: Theme): SystemStyleObject => {
  return {
    ...nonArticleRichTextInputSx(theme),
    '& p': {
      py: '2px',
      my: '2px',
      fontSize: '1rem',
    },
  };
};

/**
 * GenericItemLSS -> <p data-json="...">...</p>
 *
 * format a GenericItemLSS to an author p tag for tiptap
 * encodes the LSS item data onto the p tag as a data-json attribute
 * so over collab users can see author name without requiring fetch
 * and also allows parsing uuids off the editor content for payload
 */
export function formatTiptapAuthorTag(author: GenericItemLSS) {
  const p = document.createElement('p');
  p.setAttribute('title', author?.description || '');
  try {
    const dataJson = JSON.stringify(author);
    p.setAttribute('data-json', dataJson); // Add the attribute if JSON.stringify succeeds
  } catch (error) {
    console.warn('[formatTiptapAuthorTag] failed to stringify author:', error);
  }
  p.textContent = author?.label || '';
  const tagString = p.outerHTML; // return string <p> tag...
  return tagString;
}

/**
 * <p data-json="...">...</p> -> GenericItemLSS[]
 * extract data-json encoded on <p> tags from
 * editor and return GenericItemLSS array
 **/
export function getAuthorLSSItemsViaDataAttr(
  editorJson?: JSONContent | null,
): GenericItemLSS[] {
  if (!editorJson) {
    return [];
  }
  // extract data-json from <p> tags in editor content
  const listItems = editorJson?.content
    ?.map((item: JSONContent) => {
      try {
        // Parse JSON from attrs
        const parsed: GenericItemLSS = JSON.parse(item?.attrs?.['data-json']);
        return {
          description: parsed?.description,
          id: parsed?.id,
          path: parsed?.path,
          name: parsed?.label,
          label: parsed?.name,
        };
      } catch (error) {
        // Skip invalid entries gracefully
        return null;
      }
    })
    .filter((item) => item !== null && item.id !== undefined) as GenericItemLSS[];

  return listItems || [];
}

// used to format payload, and for form default values
// convert UserAcctSummary[] or ListSearchSelect[] items
// to just [{uuid:'xyz'},...] to match api expected format
export function getAuthorUuidObj(
  author: GenericItemLSS | UserAccountSummary,
): PayloadAuthor | null {
  const getId = author?.uuid || author?.id;
  if (!author || !getId) {
    return null; // if no author or no id return null
  }
  const uuidObj = { uuid: getId };
  return uuidObj;
}

// also used to setup form default values
// get authors uuids from LSS[] or UserAccountSummary[]
// return array of uuid objects [{ uuid: 'id' }]
export function getAuthorsUuidArray(
  authors?: GenericItemLSS[] | UserAccountSummary[],
): PayloadAuthor[] {
  if (!Array.isArray(authors) || !authors.length) {
    return [];
  }
  const payloadAuthors = authors
    .map(getAuthorUuidObj)
    .filter((author): author is PayloadAuthor => {
      const isBad = !author || !author?.uuid;
      if (isBad) {
        return false;
      }
      return true;
    });
  return payloadAuthors;
}

// Authors serializes a LSS item onto the <p> tag as data-json
// so that they can be communicated across users in collab mode
// and also is where we extract the uuids for the payload.
// Serialization so that the other client does not need to fetch
// to resolve the authors individually, but for dirty diff purposes
// reset the default form field values during init.

export const Authors = memo(
  ({ wrapperSx, showLabel = true, inputSx, label }: AuthorsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dataProvider = useDataProvider();
    const { setValue, getValues } = useFormContext();
    const { isReadOnlyMode, recordCopy, isCreateMode } = useAssetConfigContext();
    const {
      authorsEditor,
      authorsEditorConfig,
      authorsEditorHook,
      bylineEditor,
      taglineEditor,
    } = useAssetEditorsContext();
    const { hasSetInitContent } = authorsEditorHook;
    const [localInit, setLocalInit] = useState(false);
    const [authorsList, setAuthorsList] = useState<GenericItemLSS[]>([]);
    const fetchedAuthorsRef = useRef<Record<string, UserAccountSummary>>({});
    const autoSetBylineRef = useRef(false);
    const autoSetTaglineRef = useRef(false);
    const hasRunCreateInitRef = useRef(false);
    const currentByline = bylineEditor?.getText()?.trim() || '';
    const currentTagline = taglineEditor?.getText()?.trim() || '';

    // Set byline/tagline if not already populated
    const autoSetField = useCallback(
      (field: 'byline' | 'tagline', value?: string) => {
        if (!value) {
          return;
        }
        const isByline = field === 'byline';
        const current = isByline ? currentByline : currentTagline;
        if (value && !current) {
          const ref = isByline ? autoSetBylineRef : autoSetTaglineRef;
          ref.current = true;
          setValue(field, value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          const editor = isByline ? bylineEditor : taglineEditor;
          editor?.commands.setContent(value);
        }
      },
      [currentByline, currentTagline, setValue, bylineEditor, taglineEditor],
    );

    // Callbacks to auto set byline/tagline from first author if not already set when authors change
    const autoSetByline = useCallback(
      (byline?: string) => autoSetField('byline', byline),
      [autoSetField],
    );
    const autoSetTagline = useCallback(
      (tagline?: string) => autoSetField('tagline', tagline),
      [autoSetField],
    );

    // handle changes to authors editor, set to local state and form
    const handleAuthorsOnChange = useCallback<CustomEditorOnChangeFn>(
      ({ editor }) => {
        if (!authorsEditor || !hasSetInitContent) {
          return;
        }
        const editorJson = editor?.getJSON();
        const editorAuthorsLSS: GenericItemLSS[] =
          getAuthorLSSItemsViaDataAttr(editorJson);
        const newAuthorsPayload = getAuthorsUuidArray(editorAuthorsLSS);
        const currentAuthorsPayload = getValues('authors');
        const hasDiff = !isDeepEqual(newAuthorsPayload, currentAuthorsPayload);
        // Update state and form if dirty diff detected
        if (hasDiff || (isCreateMode && !hasRunCreateInitRef.current)) {
          setAuthorsList(editorAuthorsLSS);
          setValue('authors', newAuthorsPayload, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });

          // Fetch account when authors are changed and either byline or tagline is empty
          if (
            ((!currentByline && !autoSetBylineRef.current) ||
              (!currentTagline && !autoSetTaglineRef.current)) &&
            newAuthorsPayload.length > 0
          ) {
            const firstAuthorUuid = newAuthorsPayload[0]?.uuid;
            if (firstAuthorUuid) {
              const cached = fetchedAuthorsRef.current[firstAuthorUuid];
              if (cached) {
                autoSetByline(cached.byline);
                autoSetTagline(cached.tagline);
              } else {
                dataProvider
                  .getUserAccountById(firstAuthorUuid)
                  .then((result: UserAccountSummary) => {
                    fetchedAuthorsRef.current[firstAuthorUuid] = result;
                    autoSetByline(result.byline);
                    autoSetTagline(result.tagline);
                  })
                  .catch((error: any) => {
                    console.error(
                      'Failed to fetch user account for first author:',
                      error,
                    );
                  });
              }
            }
          }
        }
      },
      [
        authorsEditor,
        hasSetInitContent,
        setValue,
        getValues,
        dataProvider,
        currentByline,
        currentTagline,
        autoSetByline,
        autoSetTagline,
        isCreateMode,
      ],
    );

    // upon the dialog open/close perform a fresh resync
    // via the live editor in case some event was missed
    const handleDialogOpen = useCallback(
      (event?: React.SyntheticEvent) => {
        if (!authorsEditor || !localInit) {
          return;
        }
        handleAuthorsOnChange({ editor: authorsEditor });
      },
      [authorsEditor, localInit, handleAuthorsOnChange],
    );

    const handleDialogClose = useCallback(
      (event?: React.SyntheticEvent) => {
        if (!authorsEditor || !localInit) {
          return;
        }
        handleAuthorsOnChange({ editor: authorsEditor });
      },
      [authorsEditor, localInit, handleAuthorsOnChange],
    );

    const richTextInputSx = useMemo(() => {
      return inputSx ? inputSx : getBaseRichTextInputSx(theme);
    }, [theme, inputSx]);

    // Filters for account type
    const authorFilterConfig = useMemo(
      () => ({
        options: [
          {
            value: 'admin',
            label: t('blox.user.account_type.admin'),
            paramKey: 'account_type',
            paramValue: 'admin',
          },
          {
            value: 'normal',
            label: t('blox.user.account_type.normal'),
            paramKey: 'account_type',
            paramValue: 'normal',
          },
          {
            value: 'any',
            label: t('blox.user.account_type.any'),
            paramKey: 'account_type',
          }, // no paramValue removes the filter
        ],
        defaultValue: 'admin',
      }),
      [t],
    );

    // Reset autoSetBylineRef/taglineRef if fields are removed
    useEffect(() => {
      if (!currentByline && autoSetBylineRef.current) {
        autoSetBylineRef.current = false;
      }
    }, [currentByline]);

    useEffect(() => {
      if (!currentTagline && autoSetTaglineRef.current) {
        autoSetTaglineRef.current = false;
      }
    }, [currentTagline]);

    // Initial sync of authors and auto byline
    useEffect(() => {
      if (localInit) {
        return; // skip if already init
      }
      if (!authorsEditor || !hasSetInitContent) {
        return; // await full editor init
      }
      const editorJson = authorsEditor?.getJSON();
      // compare current form state to editor authors in same formats
      const editorAuthorsLss = getAuthorLSSItemsViaDataAttr(editorJson);
      const editorAuthorsPayload = getAuthorsUuidArray(editorAuthorsLss);
      const formsCurrentAuthors = getValues('authors');

      const hasDiff = !isDeepEqual(editorAuthorsPayload, formsCurrentAuthors);
      // init setup local state copy
      setAuthorsList(editorAuthorsLss);
      // if was diff then update formValues
      if (hasDiff) {
        setValue('authors', editorAuthorsPayload, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
      // set local state initialized to prevent rerun
      setLocalInit(true);

      // If in create mode set byline from placed default author
      if (isCreateMode) {
        handleAuthorsOnChange({ editor: authorsEditor });
        hasRunCreateInitRef.current = true;
      }
    }, [
      authorsEditor,
      hasSetInitContent,
      localInit,
      setValue,
      getValues,
      recordCopy,
      dataProvider,
      isCreateMode,
      handleAuthorsOnChange,
    ]);

    if (!localInit || !authorsEditor || !hasSetInitContent || !authorsEditorConfig) {
      return null;
    }

    return (
      <Box
        id="authors-wrapper"
        data-testid="authors-wrapper"
        sx={{
          ...(wrapperSx
            ? typeof wrapperSx === 'function'
              ? wrapperSx(theme)
              : wrapperSx
            : {}),
        }}
      >
        {showLabel && (
          <Typography sx={{ ...richTextLabelTypographySx, mb: 1 }}>
            {label || t('blox.editorial.asset.authors.label')}
          </Typography>
        )}

        <InputSelectDialog
          title={t('blox.editorial.asset.authors.label')}
          editor={authorsEditor}
          disabled={isReadOnlyMode}
          onDialogOpen={handleDialogOpen}
          onDialogClose={handleDialogClose}
          inputComponent={
            <RichTextInput
              sx={richTextInputSx}
              editor={authorsEditor}
              id={authorsEditorConfig?.id}
              type={authorsEditorConfig?.type}
              name={authorsEditorConfig?.name}
              onEditorChange={handleAuthorsOnChange}
              isDataPrivate={true}
              disabled={isReadOnlyMode}
            />
          }
          contentComponent={
            <ListSearchSelect
              label={label || t('blox.editorial.asset.authors.label')}
              listTarget="authors"
              selections={authorsList}
              isDataPrivate={true}
              disabled={isReadOnlyMode}
              filterConfig={authorFilterConfig}
              authorDisplay={true}
              dndReorder={true}
              helpText={t('blox.editorial.asset.authors.help_reorder')}
              primaryTooltipText={t('blox.editorial.asset.authors.primary_tooltip')}
            />
          }
        />
      </Box>
    );
  },
);
Authors.displayName = 'Authors';
export default Authors;

/**
 *
 * File Notes:
 *
 * one thing to consider is that in the specific case of collab mode
 * the useEffects that sets up the display of the authors tags
 * would be setting it up from whatever content was in the editor...
 *
 * but actually an authors name/lastname/etc may have changed since the
 * last time the ydoc was 'updated'... and our current check only does
 * a last update time check to determine if the ydoc was updated or not...
 *
 * so if a user changes their profile information in blue
 * but we dont actually do any saves which update time on the bloxdoc
 * then the ydoc might have stale/outdated information in it
 * until the next save back to blue or refresh of the ydoc...
 *
 * one way to potentially remedy this is possibly
 * to do a check, when this component mounts to see if the
 * authors uuids on blox record maybe their names have changed
 * then this component can sort of reconcile the displayed
 * editors content <p> tags with the new info, if new info exists, but wont
 * set dirty state on the form as the uuids/selections have not changed.
 *
 * Also could be done in the useCreateEditor for authors specifically
 * with some pre editor return callback fn passed as a prop, and we could
 * additionally sync the entire blox record to another hidden field on
 * the ydoc in order to help with the reconciliation process. As well
 * as deleting the ydoc on the backend side once all users have left, so
 * the next load would be a fully fresh initialization. Need to be careful
 * with that though as we dont want to delete if someone was just idle...
 *
 */

/**
 *
 * Alternative to useEffect and useState to sync authors
 * if having odd initial state or collab issue we can try
 * use useSyncExternalStore as the authors state instead...
 *
 * Either use a useRef to keep the snap vs newSnap
 * and useCallback the 'callback' to avoid rerenders
 * alternatively have it return a primitive and
 * parse it back to an object in the component.
 *
 * useSyncExternalStore just needs a stable comparable
 * value since it caches result so either stringify/parse or
 * useref to keep a some stable ref to the result value
 *
 * const snapshotRef = useRef<GenericItemLSS[]>([]); // alt to stringify/parse
 * const authorsExtracted = useSyncExternalStore(
 *   (callback) => {
 *     if (!authorsEditor || !hasSetInitContent) return () => {}; // no-op if not ready
 *     let snap = getAuthorLSSItemsViaDataAttr(authorsEditor.getJSON());
 *     let snapStr = JSON.stringify(snap);
 *     const handleChanges = funnel(
 *       () => {
 *         const newSnap = getAuthorLSSItemsViaDataAttr(authorsEditor.getJSON());
 *         const newSnapStr = JSON.stringify(newSnap);
 *         if (!isDeepEqual(snapStr, newSnapStr)) {
 *           snap = newSnap; // update prev snapshot
 *           const newPayload = newSnap
 *             ?.filter((author) => author?.id)
 *             ?.map((author) => ({ uuid: author.id }));
 *           // set new changes to form
 *           setValue('authors', newPayload, shouldValidDirtyTouch);
 *           callback();
 *         }
 *       },
 *       { minQuietPeriodMs: 100 },
 *     );
 *     authorsEditor.on('update', handleChanges?.call);
 *     return () => {
 *       authorsEditor.off('update', handleChanges?.call);
 *       handleChanges?.flush?.();
 *     };
 *   },
 *   () => {
 *     const init = JSON.stringify(getAuthorLSSItemsViaDataAttr(authorsEditor?.getJSON()));
 *     return init;
 *   },
 * );
 * const authorsLSS: GenericItemLSS[] = useMemo(() => {
 *   try { return JSON.parse(authorsExtracted) as GenericItemLSS[]; }
 *   catch(e) { console.debug('[Authors] failed to parse:', e);  return []; }
 * }, [authorsExtracted]);
 *
 *
 */
